import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { logError } from '../utils/log';

const gameStructure = [
    { name: '301 Singles', type: 'S301' }, { name: '301 Singles', type: 'S301' }, { name: '301 Doubles', type: 'D301' },
    { name: '501 Singles', type: 'S501' }, { name: '501 Singles', type: 'S501' }, { name: '501 Doubles', type: 'D501' },
    { name: 'Cricket Singles', type: 'SCricket' }, { name: 'Cricket Singles', type: 'SCricket' }, { name: 'Cricket Doubles', type: 'DCricket' },
];

export const TeamStatsPage = () => {
    const [teamStats, setTeamStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('game');
    const [sortConfig, setSortConfig] = useState({ key: 'gamePoints', direction: 'desc' });
    const [currentSchedule, setCurrentSchedule] = useState(null);
    const [allSchedules, setAllSchedules] = useState([]);
    const [selectedScheduleId, setSelectedScheduleId] = useState('');

    useEffect(() => {
        document.body.classList.add('stats-lock-x');
        document.documentElement.classList.add('stats-lock-x');
        return () => {
            document.body.classList.remove('stats-lock-x');
            document.documentElement.classList.remove('stats-lock-x');
        };
    }, []);

    useEffect(() => {
        const fetchSchedules = async () => {
            setLoading(true);
            try {
                const schedulesSnapshot = await getDocs(collection(db, "schedules"));
                const schedulesList = schedulesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                schedulesList.sort((a, b) => {
                    if (a.archived !== b.archived) return a.archived ? 1 : -1;
                    return new Date(b.createdAt) - new Date(a.createdAt);
                });
                setAllSchedules(schedulesList);

                if (schedulesList.length > 0) {
                    const latestNonArchived = schedulesList.find(s => !s.archived);
                    setSelectedScheduleId(latestNonArchived ? latestNonArchived.id : schedulesList[0].id);
                } else {
                    setLoading(false);
                }
            } catch (error) {
                logError("Error fetching schedules: ", error);
                setLoading(false);
            }
        };
        fetchSchedules();
    }, []);

    useEffect(() => {
        if (!selectedScheduleId || allSchedules.length === 0) {
            if (allSchedules.length > 0) setLoading(false);
            return;
        }
        const calculateTeamStats = async () => {
            setLoading(true);
            try {
                const scheduleForStats = allSchedules.find(s => s.id === selectedScheduleId);
                setCurrentSchedule(scheduleForStats);

                if (!scheduleForStats) {
                    setTeamStats([]);
                    setLoading(false);
                    return;
                }

                const teamsSnapshot = await getDocs(collection(db, 'teams'));
                const allTeamsData = teamsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                const participatingTeamIds = new Set();
                if (Array.isArray(scheduleForStats.teams) && scheduleForStats.teams.length > 0) {
                    scheduleForStats.teams.forEach(tid => tid && participatingTeamIds.add(tid));
                } else {
                    (scheduleForStats.weeks || []).forEach(week => {
                        (week.games || []).forEach(match => {
                            if (match.homeTeamId && match.homeTeamId !== 'TBD') participatingTeamIds.add(match.homeTeamId);
                            if (match.awayTeamId && match.awayTeamId !== 'TBD') participatingTeamIds.add(match.awayTeamId);
                        });
                    });
                }
                const teamsData = allTeamsData.filter(t => participatingTeamIds.has(t.id));

                const statsData = {};
                teamsData.forEach(team => {
                    statsData[team.id] = {
                        id: team.id,
                        name: team.name,
                        gamePoints: 0,
                        matchWins: 0,
                        matchLosses: 0,
                        weeksPlayed: new Set(),
                        S301: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                        D301: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                        S501: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                        D501: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                        SCricket: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                        DCricket: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                        totalHighlights: 0,
                        totalTons: 0,
                        highestTon: 0,
                        total180s: 0,
                        totalHighIns: 0,
                        highestIn: 0,
                        totalHighOuts: 0,
                        highestOut: 0,
                        totalC6: 0,
                        totalC7: 0,
                        totalC8: 0,
                        totalC9: 0,
                    };
                });

                if (scheduleForStats) {
                    (scheduleForStats.weeks || []).forEach(week => {
                        (week.games || []).forEach(match => {
                            if (!match.results) return;
                            const homeTeamId = match.homeTeamId;
                            const awayTeamId = match.awayTeamId;

                            const weekId = `${scheduleForStats.id}-${week.week}`;
                            if (statsData[homeTeamId]) statsData[homeTeamId].weeksPlayed.add(weekId);
                            if (statsData[awayTeamId]) statsData[awayTeamId].weeksPlayed.add(weekId);

                            let homeMatchGamePoints = 0;
                            let awayMatchGamePoints = 0;

                            (match.results.gameScores || []).forEach((score, idx) => {
                                const gameType = gameStructure[idx]?.type;
                                if (!gameType) return;
                                const gameWonByHome = (score.home || 0) > (score.away || 0);

                                const homeEntry = statsData[homeTeamId];
                                const awayEntry = statsData[awayTeamId];
                                if (homeEntry) {
                                    homeEntry[gameType].legsWon += (score.home || 0);
                                    homeEntry[gameType].legsPlayed += (score.home || 0) + (score.away || 0);
                                    if (gameWonByHome) homeEntry[gameType].w++; else homeEntry[gameType].l++;
                                }
                                if (awayEntry) {
                                    awayEntry[gameType].legsWon += (score.away || 0);
                                    awayEntry[gameType].legsPlayed += (score.home || 0) + (score.away || 0);
                                    if (!gameWonByHome) awayEntry[gameType].w++; else awayEntry[gameType].l++;
                                }

                                if (gameWonByHome) homeMatchGamePoints++; else awayMatchGamePoints++;
                            });

                            if (statsData[homeTeamId]) statsData[homeTeamId].gamePoints += homeMatchGamePoints;
                            if (statsData[awayTeamId]) statsData[awayTeamId].gamePoints += awayMatchGamePoints;
                            if (homeMatchGamePoints > awayMatchGamePoints) {
                                if (statsData[homeTeamId]) statsData[homeTeamId].matchWins++;
                                if (statsData[awayTeamId]) statsData[awayTeamId].matchLosses++;
                            } else if (awayMatchGamePoints > homeMatchGamePoints) {
                                if (statsData[awayTeamId]) statsData[awayTeamId].matchWins++;
                                if (statsData[homeTeamId]) statsData[homeTeamId].matchLosses++;
                            }

                            const assignmentMap = {};
                            Object.keys(match.results.playerAssignments || {}).forEach(key => {
                                const playerId = match.results.playerAssignments[key];
                                const isHome = key.includes('home');
                                assignmentMap[playerId] = isHome ? homeTeamId : awayTeamId;
                            });

                            const processedPlayers = new Set();
                            const highlightsByPlayer = match.results.highlights || {};
                            Object.keys(highlightsByPlayer).forEach(playerId => {
                                if (processedPlayers.has(playerId)) return;
                                const teamId = assignmentMap[playerId];
                                const entry = statsData[teamId];
                                if (!entry) return;
                                const h = highlightsByPlayer[playerId] || {};
                                let delta = 0;

                                if (h.tons) {
                                    const tons = h.tons.split(',').map(t => parseInt(t.trim(), 10)).filter(n => !isNaN(n));
                                    entry.totalTons += tons.length; delta += tons.length;
                                    const maxTon = Math.max(0, ...tons);
                                    if (maxTon > entry.highestTon) entry.highestTon = maxTon;
                                }
                                if (h['180s']) {
                                    const c = parseInt(h['180s'], 10);
                                    if (!isNaN(c)) { entry.total180s += c; delta += c; }
                                }
                                if (h.highIn || h.on80) {
                                    const ins = (h.highIn || h.on80).split(',').map(t => parseInt(t.trim(), 10)).filter(n => !isNaN(n));
                                    entry.totalHighIns += ins.length; delta += ins.length;
                                    const maxIn = Math.max(0, ...ins);
                                    if (maxIn > entry.highestIn) entry.highestIn = maxIn;
                                }
                                if (h.highOut) {
                                    const outs = h.highOut.split(',').map(t => parseInt(t.trim(), 10)).filter(n => !isNaN(n));
                                    entry.totalHighOuts += outs.length; delta += outs.length;
                                    const maxOut = Math.max(0, ...outs);
                                    if (maxOut > entry.highestOut) entry.highestOut = maxOut;
                                }
                                ['c9','C9','c8','C8','c7','C7','c6','C6'].forEach(key => {
                                    const val = parseInt(h[key] ?? '0', 10);
                                    if (!isNaN(val) && val > 0) {
                                        if (key.toLowerCase() === 'c9') entry.totalC9 += val;
                                        else if (key.toLowerCase() === 'c8') entry.totalC8 += val;
                                        else if (key.toLowerCase() === 'c7') entry.totalC7 += val;
                                        else if (key.toLowerCase() === 'c6') entry.totalC6 += val;
                                        delta += val;
                                    }
                                });

                                entry.totalHighlights += delta;
                                processedPlayers.add(playerId);
                            });
                        });
                    });
                }

                const statsArray = Object.values(statsData);
                statsArray.forEach(team => {
                    team.weeksPlayed = team.weeksPlayed.size;
                    ['S301','D301','S501','D501','SCricket','DCricket'].forEach(key => {
                        const totalLegs = team[key].legsPlayed;
                        team[key].winPct = totalLegs > 0 ? (team[key].legsWon / totalLegs) : 0;
                    });
                });

                setTeamStats(statsArray);
            } catch (error) {
                logError('Error calculating team stats: ', error);
            }
            setLoading(false);
        };
        calculateTeamStats();
    }, [selectedScheduleId, allSchedules]);

    const sortedTeamStats = React.useMemo(() => {
        const items = [...teamStats];
        if (sortConfig) {
            items.sort((a, b) => {
                const keys = sortConfig.key.split('.');
                const aValue = keys.reduce((obj, k) => obj && obj[k], a);
                const bValue = keys.reduce((obj, k) => obj && obj[k], b);
                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;

                // If primary sort key is gamePoints and they are tied, use head-to-head as a tie-breaker.
                if (sortConfig.key === 'gamePoints') {
                    if (currentSchedule && currentSchedule.weeks) {
                        for (const week of currentSchedule.weeks) {
                            for (const game of week.games) {
                                const isMatch = (game.homeTeamId === a.id && game.awayTeamId === b.id) || (game.homeTeamId === b.id && game.awayTeamId === a.id);
                                if (isMatch && game.results && game.results.matchStats) {
                                    const homePoints = game.results.matchStats.home.gamePoints;
                                    const awayPoints = game.results.matchStats.away.gamePoints;
                                    if (homePoints !== awayPoints) {
                                        const winnerId = homePoints > awayPoints ? game.homeTeamId : game.awayTeamId;
                                        if (winnerId === a.id) return -1; // a wins
                                        if (winnerId === b.id) return 1;  // b wins
                                    }
                                }
                            }
                        }
                    }
                }
                return a.name.localeCompare(b.name); // Final alphabetical sort for stability
            });
        }
        return items;
    }, [teamStats, sortConfig, currentSchedule]);

    const requestSort = (key) => {
        let direction = 'desc';
        if (sortConfig.key === key && sortConfig.direction === 'desc') direction = 'asc';
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key === key) return sortConfig.direction === 'desc' ? ' ▼' : ' ▲';
        return '';
    };

    if (loading) return <div className="text-center">Loading stats...</div>;

    return (
        <div className="space-y-4 overflow-x-hidden">
            <h2 className="page-title heading-primary-1">Team Statistics</h2>

            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex gap-2 items-center w-full sm:w-auto">
                    <label htmlFor="season-select" className="text-sm font-semibold text-muted whitespace-nowrap">Season:</label>
                    <select
                        id="season-select"
                        value={selectedScheduleId}
                        onChange={e => setSelectedScheduleId(e.target.value)}
                        className="select-field p-2 w-full sm:w-auto"
                        disabled={loading || allSchedules.length === 0}
                    >
                        {allSchedules.map(s => (
                            <option key={s.id} value={s.id}>
                                {s.name} {s.archived ? '(Archived)' : ''}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            {currentSchedule?.archived && (<p className="text-center text-sm text-warning -mt-4">You are viewing stats for an archived season.</p>)}

            <div className="flex flex-wrap justify-center gap-2 mb-4">
                <button onClick={() => setView('game')} className={`px-4 py-2 text-sm font-medium rounded-md ${view === 'game' ? 'segmented-btn-active' : 'segmented-btn'}`}>Game View</button>
                <button onClick={() => setView('highlight')} className={`px-4 py-2 text-sm font-medium rounded-md ${view === 'highlight' ? 'segmented-btn-active' : 'segmented-btn'}`}>Highlight View</button>
                <button onClick={() => setView('all')} className={`px-4 py-2 text-sm font-medium rounded-md ${view === 'all' ? 'segmented-btn-active' : 'segmented-btn'}`}>View All</button>
            </div>

            <div className="card rounded-lg shadow-lg p-0 overflow-hidden">
                <div className="table-wrap">
                    <table className="min-w-max sm:min-w-full">
                    <thead className="table-header">
                        <tr>
                            <th rowSpan="2" className="sticky left-0 sticky-cell px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider cursor-pointer z-20">Team{getSortIndicator('name')}</th>
                            {(view === 'game' || view === 'all') && (
                                <>
                                    <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('weeksPlayed')}>WP{getSortIndicator('weeksPlayed')}</th>
                                    <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('matchWins')}>Match W-L{getSortIndicator('matchWins')}</th>
                                    <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('gamePoints')}>Game Points{getSortIndicator('gamePoints')}</th>
                                    <th colSpan="6" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider border-low">301</th>
                                    <th colSpan="6" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider border-low">501</th>
                                    <th colSpan="6" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider">Cricket</th>
                                </>
                            )}
                            {(view === 'highlight' || view === 'all') && (
                                <>
                                    <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer border-low" onClick={() => requestSort('totalHighlights')}>Highlights{getSortIndicator('totalHighlights')}</th>
                                    <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('totalTons')}>Ton+{getSortIndicator('totalTons')}</th>
                                    <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('highestTon')}>High Ton{getSortIndicator('highestTon')}</th>
                                    <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('total180s')}>180s{getSortIndicator('total180s')}</th>
                                    <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('highestIn')}>High In{getSortIndicator('highestIn')}</th>
                                    <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('totalHighIns')}># High Ins{getSortIndicator('totalHighIns')}</th>
                                    <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('highestOut')}>High Out{getSortIndicator('highestOut')}</th>
                                    <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('totalHighOuts')}># High Outs{getSortIndicator('totalHighOuts')}</th>
                                    <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('totalC9')}>9-Hits{getSortIndicator('totalC9')}</th>
                                    <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('totalC8')}>8-Hits{getSortIndicator('totalC8')}</th>
                                    <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('totalC7')}>7-Hits{getSortIndicator('totalC7')}</th>
                                    <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('totalC6')}>6-Hits{getSortIndicator('totalC6')}</th>
                                </>
                            )}
                        </tr>
                        {(view === 'game' || view === 'all') && (
                            <tr>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider border-low cursor-pointer" onClick={() => requestSort('S301.w')}>S (W-L){getSortIndicator('S301.w')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('S301.legsWon')}>S (LEGS){getSortIndicator('S301.legsWon')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('S301.winPct')}>S Win%{getSortIndicator('S301.winPct')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('D301.w')}>D (W-L){getSortIndicator('D301.w')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('D301.legsWon')}>D (LEGS){getSortIndicator('D301.legsWon')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider border-low cursor-pointer" onClick={() => requestSort('D301.winPct')}>D Win%{getSortIndicator('D301.winPct')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('S501.w')}>S (W-L){getSortIndicator('S501.w')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('S501.legsWon')}>S (LEGS){getSortIndicator('S501.legsWon')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('S501.winPct')}>S Win%{getSortIndicator('S501.winPct')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('D501.w')}>D (W-L){getSortIndicator('D501.w')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('D501.legsWon')}>D (LEGS){getSortIndicator('D501.legsWon')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider border-low cursor-pointer" onClick={() => requestSort('D501.winPct')}>D Win%{getSortIndicator('D501.winPct')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('SCricket.w')}>S (W-L){getSortIndicator('SCricket.w')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('SCricket.legsWon')}>S (LEGS){getSortIndicator('SCricket.legsWon')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('SCricket.winPct')}>S Win%{getSortIndicator('SCricket.winPct')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('DCricket.w')}>D (W-L){getSortIndicator('DCricket.w')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('DCricket.legsWon')}>D (LEGS){getSortIndicator('DCricket.legsWon')}</th>
                                <th className="px-2 py-2 text-center text-xs font-medium text-muted uppercase tracking-wider cursor-pointer" onClick={() => requestSort('DCricket.winPct')}>D Win%{getSortIndicator('DCricket.winPct')}</th>
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {sortedTeamStats.map(team => (
                            <tr key={team.id} className="row-alt">
                                <td className="sticky left-0 sticky-cell px-4 py-4 whitespace-nowrap text-sm font-semibold text-strong z-10">{team.name}</td>
                                {(view === 'game' || view === 'all') && (
                                    <>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.weeksPlayed}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.matchWins}-{team.matchLosses}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center font-bold subheading-primary-1">{team.gamePoints}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted border-low">{team.S301.w}-{team.S301.l}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.S301.legsWon}-{(team.S301.legsPlayed || 0) - (team.S301.legsWon || 0)}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.S301.winPct.toFixed(3)}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.D301.w}-{team.D301.l}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.D301.legsWon}-{(team.D301.legsPlayed || 0) - (team.D301.legsWon || 0)}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted border-low">{team.D301.winPct.toFixed(3)}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.S501.w}-{team.S501.l}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.S501.legsWon}-{(team.S501.legsPlayed || 0) - (team.S501.legsWon || 0)}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.S501.winPct.toFixed(3)}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.D501.w}-{team.D501.l}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.D501.legsWon}-{(team.D501.legsPlayed || 0) - (team.D501.legsWon || 0)}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted border-low">{team.D501.winPct.toFixed(3)}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.SCricket.w}-{team.SCricket.l}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.SCricket.legsWon}-{(team.SCricket.legsPlayed || 0) - (team.SCricket.legsWon || 0)}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.SCricket.winPct.toFixed(3)}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.DCricket.w}-{team.DCricket.l}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.DCricket.legsWon}-{(team.DCricket.legsPlayed || 0) - (team.DCricket.legsWon || 0)}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.DCricket.winPct.toFixed(3)}</td>
                                    </>
                                )}
                                {(view === 'highlight' || view === 'all') && (
                                    <>
                                        <td className={`px-4 py-4 whitespace-nowrap text-sm text-center font-bold subheading-primary-1 ${view === 'highlight' ? 'border-low' : ''}`}>{team.totalHighlights}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.totalTons}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.highestTon > 0 ? team.highestTon : '-'}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.total180s}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.highestIn > 0 ? team.highestIn : '-'}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.totalHighIns}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.highestOut > 0 ? team.highestOut : '-'}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.totalHighOuts}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.totalC9}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.totalC8}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.totalC7}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-muted">{team.totalC6}</td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default TeamStatsPage;